import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { queryKeys, refetchConfigMedicationQueries } from "../../queries";
import Sidebar from "../../components/Sidebar";
import DashboardDoctorHeader from "../../components/MainHeader/DashboardDoctorHeader";
import icon1 from "../../assets/images/setting-icon.svg";
import pencilImage from "../../assets/images/pencil.png";
import removeIcon from "../../assets/images/remove-icon3.svg";
import cancelIcon from "../../assets/images/cancel-btn.svg";
import TestManagement from "../../components/TestManagement";
import MedicamentManagement from "../../components/MedicamentManagement";
import MedicationConfigurationManagement from "../../components/MedicationConfigurationManagement";
import SpecialTemplatesManagement from "../../components/SpecialTemplatesManagement";
import SpecialTemplatesDownloadModal from "../../components/consultation/tabs/SpecialTemplatesDownloadModal";
import ProtocolPrescriptorComposer from "../../components/consultation/tabs/ProtocolPrescriptorComposer";
import { formatDateInDayDateYear } from "../../utils/commonData";
import {
  addConfigMedicationApi,
  addMedicineApi,
  addProtocolApi,
  addTestApi,
  deleteConfigMedicationApi,
  deleteDoctorNewTemplateApi,
  deleteMedicineApi,
  deleteProtocolApi,
  downloadDoctorTemplateApi,
  createPrescriptorPrescriptionApi,
  fetchConfigMedicationApi,
  fetchDoctorInfoApi,
  fetchMedicineApi, fetchMedicineeApi,
  fetchProtocolApi,
  getAdminAllTemplateApi,
  getDoctorAllTemplateApi,
  searchProtocolTemplatesByNameApi,
  searchMedicinesDetailsApi,
  searchMedicalTestApi,
  updateDoctorTempDetailsApi,
  updateMedicineApi,
  updateProtocolApi,
  updateTemplateOrdersApi,
} from "../../services/apiService";
import { toast } from "react-toastify";
import LoadingPage from "../../components/LoadingPage";
import "grapesjs/dist/css/grapes.min.css";
import { IMAGE_URL } from "../../app_url";
import { useNavigate } from "react-router";
import { CircularProgress } from "@mui/material";
import JoditEditor from "jodit-react";
import { useUserContext } from "../../context/UserContext";
import editIcon from "../../assets/images/edit-icon.svg";
import deleteIcon from "../../assets/images/delete-icon.svg";

// Helper pour nettoyer le HTML
function stripHtml(html) {
  const tmp = document.createElement("DIV");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
}

const LEGACY_KEY_TO_TYPE = {
  days: "frequency",
  periods: "meal",
  moments: "moment",
};

/** Libellé recherche : nom — marque — forme — dosage — conditionnement */
function formatMedicineSearchLabel(item) {
  const parts = [
    item.name || item.denomination,
    item.brand,
    item.forms,
    item.dosage,
    item.conditionnement,
  ].filter((p) => p != null && String(p).trim() !== "");

  if (parts.length) {
    return parts.join(" ").replace(/-/g, ' ');
  }
  return "";
}

const useOnClickOutside = (ref, handler) => {
  useEffect(() => {
    const listener = event => {
      if (!ref.current || ref.current.contains(event.target)) {
        return; // Click is inside the element, do nothing.
      }
      handler(); // Click is outside, call the handler.
    };

    document.addEventListener("mousedown", listener);
    return () => {
      document.removeEventListener("mousedown", listener);
    };
  }, [ref, handler]);
};

const PrescriptionManagement = ({ placeholder }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { userData } = useUserContext();
  const testNameResultRef = useRef(null);
  const editor4 = useRef(null);
  const [screenLoading, setScreenLoading] = useState(false);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [templateList, setTemplateList] = useState();
  const [adminTemplateList, setAdminTemplateList] = useState([]);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showProtocolModal, setShowProtocolModal] = useState(false);
  const [selectedAdminTemplates, setSelectedAdminTemplates] = useState([]);
  const [btnDis, setBtnDis] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [showDeleteTempPopup, setShowDeleteTempPopup] = useState(false);
  const [deleteBtnDis, setDeleteBtnDis] = useState(false);
  const [showSpecialTemplatesDownloadModal, setShowSpecialTemplatesDownloadModal] = useState(false);
  const [saveDocBtnDis, setSaveDocBtnDis] = useState(false);
  const [specialTemplatesRefreshKey, setSpecialTemplatesRefreshKey] = useState(0);
  const [selectedTemplateId, setSelectedTemplateId] = useState();
  const [prescriptionContent, setPrescriptionContent] = useState("");

  const [searchTestNameTerm, setSearchTestNameTerm] = useState();
  const [showTestNameList, setShowTestNameList] = useState(false);
  const [testNameTimeoutId, setTestNameTimeoutId] = useState(null);
  const [testNameSearchLoad, setTestNameSearchLoad] = useState(false);
  const [searchTestNameList, setSearchTestNameList] = useState();
  const [addTestNameObj, setAddTestNameObj] = useState({ name: "", isAddedByMe: false });
  const [addTestBtnDis, setAddTestBtnDis] = useState(false);
  const [updateTestBtnDis, setUpdateTestBtnDis] = useState(false);
  const [deleteTestBtnDis, setDeleteTestBtnDis] = useState(false);
  const [configureMedicationList, setConfigureMedicationList] = useState();
  const [configureMedicationObj, setConfigureMedicationObj] = useState({
    selectedType: "frequency",
    value: "",
    openType: null,
    creatingType: null,
  });
  const [saveConBtnDis, setSaveConBtnDis] = useState(false);
  const [protocolName, setProtocolName] = useState();
  const [protocolList, setProtocolList] = useState();
  const [protocolSearchTerm, setProtocolSearchTerm] = useState("");
  const [protocolDraftPrescriptionId, setProtocolDraftPrescriptionId] = useState("");
  const [creatingProtocolDraft, setCreatingProtocolDraft] = useState(false);
  const [saveProBtnDis, setSaveProBtnDis] = useState(false);
  const [selectedProtocol, setSelectedProtocol] = useState();
  const [protocolEditorSeed, setProtocolEditorSeed] = useState("");
  const [protocolEditorKey, setProtocolEditorKey] = useState("protocol-new");
  const [showDeleteProtocolPopup, setShowDeleteProtocolPopup] = useState(false);
  const [showDeleteConfigurePopup, setShowDeleteConfigurePopup] = useState(false);
  const [configureDeleteMedicationObj, setConfigureDeleteMedicationObj] = useState();
  const [selectedTemplatesToInsert, setSelectedTemplatesToInsert] = useState([]);
  const [showInsertTemplateModal, setShowInsertTemplateModal] = useState(false);

  // Substitutes management
  const [expandedMedicineId, setExpandedMedicineId] = useState(null);
  const [substitutesList, setSubstitutesList] = useState([]);
  const [loadingSubstitutes, setLoadingSubstitutes] = useState(false);

  useOnClickOutside(testNameResultRef, () => setShowTestNameList(false));
  const [doctorDetailsObj, setDoctorDetailsObj] = useState({
    doctorName: "",
    speciality: "",
    regNo: "",
    clinicName: "",
    addressOne: "",
    addressTwo: "",
    phoneNumber: "",
    emailAddress: "",
  });
  const [docInfoDetails, setDocInfoDetails] = useState();

  const config4 = useMemo(
    () => ({
      readonly: false,
      placeholder: "",
    }),
    [placeholder]
  );

  const joditConfig = useMemo(() => ({
    ...config4,
    placeholder: prescriptionContent && stripHtml(prescriptionContent).trim() !== '' ? '' : "Entrez ici les détails de l'ordonnance..."
  }), [prescriptionContent, config4]);

  const toggleTemplateModal = () => {
    setShowTemplateModal(!showTemplateModal);
  };

  const closeProtocolModal = () => {
    setShowProtocolModal(false);
    setProtocolDraftPrescriptionId("");
  };

  const createProtocolDraftPrescription = async () => {
    const payload = {
      source_type: "template",
      is_template: true,
      status: "draft"
    };

    if (userData?.users?.id) {
      payload.doctor_id = userData.users.id;
    }

    const response = await createPrescriptorPrescriptionApi(payload);
    const scopedData = response?.data?.data ?? response?.data;
    if (!scopedData?.id) {
      throw new Error("Failed to create draft prescription.");
    }

    return String(scopedData.id);
  };

  const handleCheckboxChange = id => {
    setSelectedAdminTemplates(prevSelected => {
      if (prevSelected.includes(id)) {
        return prevSelected.filter(templateId => templateId !== id);
      } else {
        return [...prevSelected, id];
      }
    });
  };

  const handleSelectTemplateToInsert = id => {
    setSelectedTemplatesToInsert(prevSelected => {
      if (prevSelected.includes(id)) {
        return prevSelected.filter(templateId => templateId !== id);
      } else {
        return [...prevSelected, id];
      }
    });
  };

  const getDoctorAllTemplate = async () => {
    try {
      setTemplateLoading(true);
      let response = await getDoctorAllTemplateApi();
      if (response) {
        if (response.data.res === true) {
          setTemplateList(response.data.data);
        } else {
          toast.error(response.data.msg, { autoClose: 1500 });
        }
      }
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong in server. Please try again.", { autoClose: 1500 });
    } finally {
      setTemplateLoading(false);
    }
  };

  const getAdminAllTemplate = async () => {
    try {
      setScreenLoading(true);
      let response = await getAdminAllTemplateApi();
      if (response) {
        if (response.data.res === true) {
          setAdminTemplateList(response.data.data);
        } else {
          toast.error(response.data.msg, { autoClose: 1500 });
        }
      }
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong in server. Please try again.", { autoClose: 1500 });
    } finally {
      setScreenLoading(false);
    }
  };

  const handleDownloadDoctorTemplate = async () => {
    if (selectedAdminTemplates?.length === 0) {
      toast.warning("Please select atleast one template.", { autoClose: 1500 });
      return;
    }
    try {
      setBtnDis(true);
      const fd = new FormData();
      selectedAdminTemplates.map(item => {
        fd.append("doctor_templates_id[]", item);
      });
      let response = await downloadDoctorTemplateApi(fd);
      if (response) {
        if (response.data.res === true) {
          getDoctorAllTemplate();
          getAdminAllTemplate();
          setSelectedAdminTemplates([]);
          setShowTemplateModal(false);
        } else {
          toast.error(response.data.msg, { autoClose: 1500 });
        }
      }
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong in server. Please try again.", { autoClose: 1500 });
    } finally {
      setBtnDis(false);
    }
  };

  const handleInsertTemplateDirectly = (templateId) => {
    const template = templateList.find(t => t.id === templateId);
    if (template && template.template_body) {
      let tempPrescriptionContent = prescriptionContent || "";
      if (tempPrescriptionContent && !tempPrescriptionContent.endsWith("<br/>")) {
        tempPrescriptionContent += "<br/><br/>";
      }
      tempPrescriptionContent += template.template_body;
      setPrescriptionContent(tempPrescriptionContent);
      toast.success(`"${template.template_name}" inserted!`, { autoClose: 1500 });
    }
  };

  const handleInsertTemplates = () => {
    if (selectedTemplatesToInsert.length === 0) {
      toast.warning("Please select at least one template to insert.", { autoClose: 1500 });
      return;
    }

    let tempPrescriptionContent = prescriptionContent || "";

    selectedTemplatesToInsert.forEach(templateId => {
      const template = templateList.find(t => t.id === templateId);
      if (template && template.template_body) {
        if (tempPrescriptionContent && !tempPrescriptionContent.endsWith("<br/>")) {
          tempPrescriptionContent += "<br/><br/>";
        }
        tempPrescriptionContent += template.template_body;
      }
    });

    setPrescriptionContent(tempPrescriptionContent);
    setSelectedTemplatesToInsert([]);
    setShowInsertTemplateModal(false);
    toast.success("Template(s) inserted successfully!", { autoClose: 1500 });
  };

  // Handle drag start
  const handleDragStart = (e, index) => {
    setIsDragging(true);
    setDraggedIndex(index);
    e.dataTransfer.setData("text/plain", index); // Store the index in the drag event
  };

  // Handle drag over to allow drop
  const handleDragOver = e => {
    e.preventDefault(); // Allow the drop
  };

  // Handle drop
  const handleDrop = (e, targetIndex) => {
    e.preventDefault();
    setIsDragging(false);
    const sourceIndex = draggedIndex;
    if (sourceIndex === targetIndex) return; // No need to reorder if the same

    const updatedList = [...templateList];
    const [movedItem] = updatedList.splice(sourceIndex, 1); // Remove item from source
    handleUpdateTemplateOrders(templateList[sourceIndex]?.id, templateList[targetIndex]?.id);
    updatedList.splice(targetIndex, 0, movedItem); // Insert item at target

    setTemplateList(updatedList);
  };

  const handleUpdateTemplateOrders = async (fromId, toId) => {
    try {
      const fd = new FormData();
      fd.append("template1_id", fromId);
      fd.append("template2_id", toId);
      let response = await updateTemplateOrdersApi(fd);
      if (response) {
        if (response.data.res === true) {
          // setAdminTemplateList(response.data.data);
        } else {
          toast.error(response.data.msg, { autoClose: 1500 });
        }
      }
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong in server. Please try again.", { autoClose: 1500 });
    } finally {
    }
  };

  const handleRemoveTemplate = async () => {
    if (!selectedTemplateId) {
      toast.warning("Template is not available.", { autoClose: 1500 });
      return;
    }
    try {
      setDeleteBtnDis(true);
      const fd = new FormData();
      fd.append("id", selectedTemplateId);
      let response = await deleteDoctorNewTemplateApi(fd);
      if (response) {
        if (response.data.res === true) {
          getDoctorAllTemplate();
          getAdminAllTemplate();
          setSelectedTemplateId(null);
          setShowDeleteTempPopup(false);
        } else {
          toast.error(response.data.msg, { autoClose: 1500 });
        }
      }
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong in server. Please try again.", { autoClose: 1500 });
    } finally {
      setDeleteBtnDis(false);
    }
  };

  const handleSelectTest = selectItem => {
    if (selectItem?.user_id === userData?.users?.id) {
      setAddTestNameObj(prev => ({
        ...prev,
        id: selectItem?.id,
        name: selectItem?.name,
        isAddedByMe: true,
      }));
      setSearchTestNameTerm("");
      setShowTestNameList(false);
    } else {
      setSearchTestNameTerm(selectItem?.name);
      setShowTestNameList(false);
      setAddTestNameObj({ id: "", name: "", isAddedByMe: false });
    }
  };

  const getConfigMedicationList = async () => {
    try {
      let response = await fetchConfigMedicationApi();
      if (response) {
        if (response.data.res === true) {
          const options = response.data?.options || {};

          const mergedOptions = { ...options };
          Object.entries(LEGACY_KEY_TO_TYPE).forEach(([legacyKey, mappedType]) => {
            const legacyRows = Array.isArray(response.data?.[legacyKey]) ? response.data[legacyKey] : [];
            if (!legacyRows.length) return;

            const normalizedRows = legacyRows
              .map((row) => ({
                id: row?.id,
                type: mappedType,
                value: String(row?.[legacyKey] || "").trim(),
                slug: row?.slug,
              }))
              .filter((row) => row.value !== "");

            const existing = Array.isArray(mergedOptions[mappedType]) ? mergedOptions[mappedType] : [];
            const seen = new Set(existing.map((row) => String(row?.value || "").toLowerCase()));
            normalizedRows.forEach((row) => {
              const key = row.value.toLowerCase();
              if (!seen.has(key)) {
                existing.push(row);
                seen.add(key);
              }
            });
            mergedOptions[mappedType] = existing;
          });

          // Guard against the same value coming back several times (a global
          // default row + a user-owned row, or variants that only differ by
          // case like "Midi"/"midi"). Keep one entry per normalized value,
          // preferring the label that starts with an uppercase letter.
          const startsUpper = (value) => {
            const first = String(value || "").trim().charAt(0);
            return first !== "" && first === first.toUpperCase() && first !== first.toLowerCase();
          };
          Object.keys(mergedOptions).forEach((type) => {
            const rows = Array.isArray(mergedOptions[type]) ? mergedOptions[type] : [];
            const byKey = new Map();
            rows.forEach((row) => {
              const key = (row?.slug ? String(row.slug) : String(row?.value || "").trim()).toLowerCase();
              if (!key) return;
              const current = byKey.get(key);
              if (!current || (startsUpper(row?.value) && !startsUpper(current?.value))) {
                byKey.set(key, row);
              }
            });
            mergedOptions[type] = Array.from(byKey.values());
          });

          setConfigureMedicationList(mergedOptions);
          queryClient.setQueryData(
            queryKeys.prescriptions.configMedication(),
            mergedOptions,
          );
        } else {
          toast.error(response.data.msg, { autoClose: 1500 });
        }
      }
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong in server. Please try again.", { autoClose: 1500 });
    } finally {
    }
  };

  const handleAddConfigureMedication = async () => {
    const selectedType = String(configureMedicationObj?.selectedType || "").trim();
    const selectedValue = String(configureMedicationObj?.value || "").trim();

    if (!selectedType || !selectedValue) {
      toast.warning("Veuillez sélectionner un type et saisir une valeur.", { autoClose: 1500 });
    } else {
      try {
        setSaveConBtnDis(true);
        let formData = new FormData();
        formData.append("type", selectedType);
        formData.append("value", selectedValue);
        let response = await addConfigMedicationApi(formData);
        if (response) {
          if (response.data.res === true) {
            setConfigureMedicationObj({
              selectedType,
              value: "",
              openType: null,
              creatingType: null,
            });
            await Promise.all([
              getConfigMedicationList(),
              refetchConfigMedicationQueries(queryClient),
            ]);
            toast.success(response.data.msg || "Successfully Added", { autoClose: 1500 });
          } else {
            toast.error(response.data.msg, { autoClose: 1500 });
          }
        }
      } catch (error) {
        console.error(error);
        toast.error("Something went wrong in server. Please try again.", { autoClose: 1500 });
      } finally {
        setSaveConBtnDis(false);
      }
    }
  };

  const handleConfigureMedicationTypeChange = nextType => {
    setConfigureMedicationObj(prev => ({
      ...prev,
      selectedType: nextType,
      openType: null,
      creatingType: null,
      value: "",
    }));
    setConfigureDeleteMedicationObj(null);
  };

  const handleToggleConfigureMedicationValues = () => {
    setConfigureMedicationObj(prev => ({
      ...prev,
      openType: prev.openType === prev.selectedType ? null : prev.selectedType,
    }));
    setConfigureDeleteMedicationObj(null);
  };

  const handleStartConfigureMedicationCreation = () => {
    setConfigureMedicationObj(prev => ({
      ...prev,
      openType: null,
      creatingType: prev.selectedType,
      value: "",
    }));
    setConfigureDeleteMedicationObj(null);
  };

  const handleConfigureMedicationValueChange = value => {
    setConfigureMedicationObj(prev => ({...prev, value}));
  };

  const handleConfigureMedicationDeleteRequest = item => {
    setShowDeleteConfigurePopup(true);
    setConfigureDeleteMedicationObj({
      type: configureMedicationObj?.selectedType,
      id: item?.id,
      value: item?.value,
    });
  };

  const normalizeProtocolList = (response) => {
    const rows =
      (Array.isArray(response?.data?.data?.items) && response.data.data.items) ||
      (Array.isArray(response?.data?.data) && response.data.data) ||
      (Array.isArray(response?.data?.list) && response.data.list) ||
      [];

    return rows.map((item) => {
      const payload = item?.payload_json;
      const payloadAsString = payload && typeof payload === "object" ? JSON.stringify(payload) : "";
      return {
        ...item,
        medicine: typeof item?.medicine === "string" ? item.medicine : payloadAsString
      };
    });
  };

  const parseProtocolPayload = (content) => {
    const raw = String(content || "").trim();
    if (!raw) return null;

    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") {
        return parsed;
      }
    } catch (error) {
      return {
        format: "legacy_protocol_note_v1",
        version: 1,
        content_html: raw
      };
    }

    return null;
  };

  const getProtocolList = async (searchName = "") => {
    try {
      setScreenLoading(true);
      const doctorId = userData?.users?.id;
      const trimmedName = String(searchName || "").trim();

      let response;
      if (trimmedName) {
        response = await searchProtocolTemplatesByNameApi(trimmedName, { per_page: 200 });
      } else {
        response = await fetchProtocolApi({ doctor_id: doctorId, per_page: 200 });
      }

      if (response) {
        if (response.data.success === true || response.data.res === true) {
          const normalizedList = normalizeProtocolList(response).filter((item) => {
            if (!doctorId) return true;
            if (!item?.doctor_id) return true;
            return Number(item.doctor_id) === Number(doctorId);
          });
          setProtocolList(normalizedList);
        } else {
          toast.error(response.data.msg || response.data.message || "Failed to fetch templates.", { autoClose: 1500 });
        }
      }
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong in server. Please try again.", { autoClose: 1500 });
    } finally {
      setScreenLoading(false);
    }
  };

  const handleAddProtocol = async () => {
    if (!protocolName) {
      toast.warning("Please add protocol name.", { autoClose: 1500 });
    } else {
      try {
        setSaveProBtnDis(true);
        const sourcePrescriptionId = Number(protocolDraftPrescriptionId);
        const payloadJson = parseProtocolPayload(prescriptionContent);
        if (!sourcePrescriptionId && !payloadJson) {
          toast.warning("Please add and save protocol entries first.", { autoClose: 1500 });
          return;
        }

        const requestPayload = {
          doctor_id: userData?.users?.id,
          name: protocolName || ""
        };

        if (sourcePrescriptionId) {
          requestPayload.source_prescription_id = sourcePrescriptionId;
        }
        if (payloadJson) {
          requestPayload.payload_json = payloadJson;
        }

        let response = await addProtocolApi(requestPayload);
        if (response) {
          if (response.data.success === true || response.data.res === true) {
            setProtocolName("");
            setPrescriptionContent("");
            setSelectedProtocol(null);
            setProtocolDraftPrescriptionId("");
            setShowProtocolModal(false);
            getProtocolList(protocolSearchTerm);
            toast.success(response.data.msg || response.data.message || "Successfully Added.", { autoClose: 1500 });
          } else {
            toast.error(response.data.msg || response.data.message, { autoClose: 1500 });
          }
        }
      } catch (error) {
        console.error(error);
        toast.error("Something went wrong in server. Please try again.", { autoClose: 1500 });
      } finally {
        setSaveProBtnDis(false);
      }
    }
  };

  const handleEditProtocol = async () => {
    if (!selectedProtocol) {
      toast.warning("Protocol not found.", { autoClose: 1500 });
    } else if (!protocolName) {
      toast.warning("Please add protocol name.", { autoClose: 1500 });
    } else {
      try {
        setSaveProBtnDis(true);
        const sourcePrescriptionId = Number(protocolDraftPrescriptionId);
        const payloadJson = parseProtocolPayload(prescriptionContent);
        if (!sourcePrescriptionId && !payloadJson) {
          toast.warning("Please add and save protocol entries first.", { autoClose: 1500 });
          return;
        }

        const requestPayload = {
          name: protocolName || ""
        };

        if (sourcePrescriptionId) {
          requestPayload.source_prescription_id = sourcePrescriptionId;
        }
        if (payloadJson) {
          requestPayload.payload_json = payloadJson;
        }

        let response = await updateProtocolApi(selectedProtocol, requestPayload);
        if (response) {
          if (response.data.success === true || response.data.res === true) {
            setProtocolName("");
            setPrescriptionContent("");
            setSelectedProtocol(null);
            setProtocolDraftPrescriptionId("");
            setShowProtocolModal(false);
            getProtocolList(protocolSearchTerm);
            toast.success(response.data.msg || response.data.message || "Successfully edited.", { autoClose: 1500 });
          } else {
            toast.error(response.data.msg || response.data.message, { autoClose: 1500 });
          }
        }
      } catch (error) {
        console.error(error);
        toast.error("Something went wrong in server. Please try again.", { autoClose: 1500 });
      } finally {
        setSaveProBtnDis(false);
      }
    }
  };

  const handleRemoveProtocol = async () => {
    if (!selectedProtocol) {
      toast.warning("Protocol not found.", { autoClose: 1500 });
    } else {
      try {
        setDeleteBtnDis(true);
        let response = await deleteProtocolApi(selectedProtocol);
        if (response) {
          if (response.data.success === true || response.data.res === true) {
            setSelectedProtocol(null);
            setProtocolDraftPrescriptionId("");
            setShowDeleteProtocolPopup(false);
            getProtocolList(protocolSearchTerm);
            toast.success(response.data.msg || response.data.message || "Successfully deleted.", { autoClose: 1500 });
          } else {
            toast.error(response.data.msg || response.data.message, { autoClose: 1500 });
          }
        }
      } catch (error) {
        console.error(error);
        toast.error("Something went wrong in server. Please try again.", { autoClose: 1500 });
      } finally {
        setDeleteBtnDis(false);
      }
    }
  };

  const fetchDoctorInfo = async () => {
    try {
      let response = await fetchDoctorInfoApi();
      if (response) {
        if (response.data.res === true) {
          setDocInfoDetails(response.data.data);
          setDoctorDetailsObj({
            doctorName: response.data.data.users.temp_doctor_name || "",
            speciality: response.data.data.users.temp_speciality || "",
            regNo: response.data.data.users.temp_regno || "",
            clinicName: response.data.data.users.temp_clinic_name || "",
            addressOne: response.data.data.users.temp_address_one || "",
            addressTwo: response.data.data.users.temp_address_two || "",
            phoneNumber: response.data.data.users.temp_phone_number || "",
            emailAddress: response.data.data.users.temp_email_address || "",
          });
        } else {
          toast.error(response.data.msg, { autoClose: 1500 });
        }
      }
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong in server. Please try again.", { autoClose: 1500 });
    } finally {
    }
  };

  const handleRemoveConfigureMedication = async () => {
    if (!configureDeleteMedicationObj || !configureDeleteMedicationObj?.id) {
      toast.warning("Configure medication not found.", { autoClose: 1500 });
    } else {
      try {
        setDeleteBtnDis(true);
        let formData = new FormData();
        formData.append("id", configureDeleteMedicationObj?.id);
        formData.append("type", configureDeleteMedicationObj?.type);
        formData.append("value", configureDeleteMedicationObj?.value || "");
        let response = await deleteConfigMedicationApi(formData);
        if (response) {
          if (response.data.res === true) {
            setShowDeleteConfigurePopup(false);
            await Promise.all([
              getConfigMedicationList(),
              refetchConfigMedicationQueries(queryClient),
            ]);
            toast.success(response.data.msg || "Successfully deleted.", { autoClose: 1500 });
          } else {
            toast.error(response.data.msg, { autoClose: 1500 });
          }
        }
      } catch (error) {
        console.error(error);
        toast.error("Something went wrong in server. Please try again.", { autoClose: 1500 });
      } finally {
        setDeleteBtnDis(false);
      }
    }
  };

  const handleSaveDoctorDetails = async () => {
    if (
      !doctorDetailsObj.doctorName &&
      !doctorDetailsObj?.speciality &&
      !doctorDetailsObj?.regNo &&
      !doctorDetailsObj?.clinicName &&
      !doctorDetailsObj?.addressOne &&
      !doctorDetailsObj?.addressTwo &&
      !doctorDetailsObj?.phoneNumber &&
      !doctorDetailsObj?.emailAddress
    ) {
      toast.warning("Please enter any of doctor details.", { autoClose: 1500 });
    } else {
      try {
        setSaveDocBtnDis(true);
        let formData = new FormData();
        formData.append("temp_doctor_name", doctorDetailsObj?.doctorName || "");
        formData.append("temp_speciality", doctorDetailsObj?.speciality || "");
        formData.append("temp_regno", doctorDetailsObj?.regNo || "");
        formData.append("temp_clinic_name", doctorDetailsObj?.clinicName || "");
        formData.append("temp_address_one", doctorDetailsObj?.addressOne || "");
        formData.append("temp_address_two", doctorDetailsObj?.addressTwo || "");
        formData.append("temp_phone_number", doctorDetailsObj?.phoneNumber || "");
        formData.append("temp_email_address", doctorDetailsObj?.emailAddress || "");
        let response = await updateDoctorTempDetailsApi(formData);
        if (response) {
          if (response.data.res === true) {
            toast.success(response.data.msg || "Successfully deleted.", { autoClose: 1500 });
          } else {
            toast.error(response.data.msg, { autoClose: 1500 });
          }
        }
      } catch (error) {
        console.error(error);
        toast.error("Something went wrong in server. Please try again.", { autoClose: 1500 });
      } finally {
        setSaveDocBtnDis(false);
      }
    }
  };

  useEffect(() => {
    getDoctorAllTemplate();
    getAdminAllTemplate();
    getConfigMedicationList();
    fetchDoctorInfo();
  }, []);

  useEffect(() => {
    if (!userData?.users?.id) return;
    getProtocolList(protocolSearchTerm);
  }, [userData?.users?.id]);


  return (
    <div className="doctormain">
      <Sidebar />
      <div className="rightSec">
        <DashboardDoctorHeader />
        <div className="mainDiv">
          {screenLoading && <LoadingPage />}
          {templateLoading && <LoadingPage />}

          <div className="hdnDiv nurseTop nurseTop2 nurseTop3">
            <h2>
              <span>
                <img src={icon1} alt="Configuration icon" />
                Prescription Management
              </span>
              <span>
                <button className="createNewBtn" onClick={() => navigate(`/doctor/add-prescription-template`)}>
                  Create New
                </button>
                <button className="downloadTempBtn" onClick={() => setShowTemplateModal(true)}>
                  Download Template
                </button>
              </span>
            </h2>
          </div>
          <div className="general-config-sec presManage">
            {templateList?.length > 0 ? (
              <>

                <div className="slideContent">
                  {templateList?.map((item, index) => (
                    <div
                      className={isDragging ? "card dragging" : "card"}
                      key={index}
                      draggable
                      onDragStart={e => handleDragStart(e, index)}
                      onDragOver={handleDragOver}
                      onDrop={e => handleDrop(e, index)}>
                      <div className="image">
                        <img src={item?.template_image_url ?? ""} alt="template image" className="templateImage" />
                        <div className="overlay">
                          <div
                            className="pencilSection"
                            onClick={() => navigate(`/doctor/edit-prescription-template/${item?.id}${item?.type ? `?type=${item?.type}` : ""}`)}>
                            <img src={pencilImage} alt="Configuration icon" className="pencil" />
                          </div>
                          <div
                            className="removeSection"
                            onClick={() => {
                              setSelectedTemplateId(item?.id);
                              setShowDeleteTempPopup(true);
                            }}>
                            <img src={removeIcon} alt="Configuration icon" className="removeIcon" />
                          </div>
                        </div>
                      </div>
                      <p>{item?.template_name}</p>

                    </div>
                  ))}
                </div>
              </>
            ) : (
              !templateLoading && <div className="notResultText">No templates have been added or downloaded yet.</div>
            )}
            <div className="prescription_management_box">
              <div className="cmn_prescription_management_box">
                <h3>Doctor Details</h3>
                <div className="white_form_box">
                  <ul className="prescription_management_form_list">
                    <li>
                      <label>Doctor Name: </label>
                      <input
                        type="text"
                        placeholder="Enter doctor name"
                        className="form_control"
                        value={doctorDetailsObj?.doctorName}
                        onChange={e => setDoctorDetailsObj(prev => ({ ...prev, doctorName: e.target.value }))}
                      />
                    </li>
                    <li>
                      <label>Speciality: </label>
                      <input
                        type="text"
                        placeholder="Enter speciality"
                        className="form_control"
                        value={doctorDetailsObj?.speciality}
                        onChange={e => setDoctorDetailsObj(prev => ({ ...prev, speciality: e.target.value }))}
                      />
                    </li>
                    <li>
                      <label>Reg Number: </label>
                      <input
                        type="text"
                        placeholder="Enter reg. number"
                        className="form_control"
                        value={doctorDetailsObj?.regNo}
                        onChange={e => setDoctorDetailsObj(prev => ({ ...prev, regNo: e.target.value }))}
                      />
                    </li>
                    <li>
                      <label>Clinic Name: </label>
                      <input
                        type="text"
                        placeholder="Enter clinic name"
                        className="form_control"
                        value={doctorDetailsObj?.clinicName}
                        onChange={e => setDoctorDetailsObj(prev => ({ ...prev, clinicName: e.target.value }))}
                      />
                    </li>
                  </ul>
                  <ul className="prescription_management_form_list">
                    <li>
                      <label>Address One: </label>
                      <input
                        type="text"
                        placeholder="Enter address one"
                        className="form_control"
                        value={doctorDetailsObj?.addressOne}
                        onChange={e => setDoctorDetailsObj(prev => ({ ...prev, addressOne: e.target.value }))}
                      />
                    </li>
                    <li>
                      <label>Address Two: </label>
                      <input
                        type="text"
                        placeholder="Enter address two"
                        className="form_control"
                        value={doctorDetailsObj?.addressTwo}
                        onChange={e => setDoctorDetailsObj(prev => ({ ...prev, addressTwo: e.target.value }))}
                      />
                    </li>
                    <li>
                      <label>Phone Number: </label>
                      <input
                        type="text"
                        placeholder="Enter phone number"
                        className="form_control"
                        value={doctorDetailsObj?.phoneNumber}
                        onChange={e => setDoctorDetailsObj(prev => ({ ...prev, phoneNumber: e.target.value }))}
                      />
                    </li>
                    <li>
                      <label>Email Address: </label>
                      <input
                        type="text"
                        placeholder="Enter email address"
                        className="form_control"
                        value={doctorDetailsObj?.emailAddress}
                        onChange={e => setDoctorDetailsObj(prev => ({ ...prev, emailAddress: e.target.value }))}
                      />
                    </li>
                    <li className="btn_list saveDocDetailsBtn">
                      <button className="btn" onClick={() => handleSaveDoctorDetails()} disabled={saveDocBtnDis}>
                        Save
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="prescription_management_box">
               <SpecialTemplatesManagement refreshKey={specialTemplatesRefreshKey} userData={userData} allPatientDetails={null} onOpenDownloadModal={() => setShowSpecialTemplatesDownloadModal(true)} />
            </div>

           

            <div className="prescription_management_box" style={{ flexDirection: 'column', gap: 16 * 2/*2 rem*/, margin: '40px 0px' }}>
              <MedicamentManagement />
              <TestManagement />
            </div>

            <div className="configuration_prescription_management mainProtocolSection">
              <h3>
                Protocols
                <span>
                  <button
                    className="createNewBtn"
                    disabled={creatingProtocolDraft}
                    onClick={async () => {
                      try {
                        setCreatingProtocolDraft(true);
                        const draftPrescriptionId = await createProtocolDraftPrescription();

                        setSelectedProtocol(null);
                        setProtocolName("");
                        setPrescriptionContent("");
                        setProtocolDraftPrescriptionId(draftPrescriptionId);
                        setProtocolEditorSeed("");
                        setProtocolEditorKey(`protocol-new-${Date.now()}`);
                        setShowProtocolModal(true);
                      } catch (error) {
                        toast.error(error?.response?.data?.message || "Failed to initialize protocol draft.", { autoClose: 1500 });
                      } finally {
                        setCreatingProtocolDraft(false);
                      }
                    }}>
                    {creatingProtocolDraft ? "Please wait..." : "Add Protocol"}
                  </button>
                </span>
              </h3>
              <div className="protocol-search-row">
                <input
                  type="text"
                  className="form_control protocol-search-input"
                  placeholder="Search template by name"
                  value={protocolSearchTerm}
                  onChange={(e) => setProtocolSearchTerm(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      getProtocolList(protocolSearchTerm);
                    }
                  }}
                />
                <button className="protocol-search-btn" onClick={() => getProtocolList(protocolSearchTerm)}>
                  Search
                </button>
                <button
                  className="protocol-clear-btn"
                  onClick={() => {
                    setProtocolSearchTerm("");
                    getProtocolList("");
                  }}>
                  Clear
                </button>
              </div>
              <div className="upcomingAppoinment nurseDiv protocolSection">
                <div className="nurseList">
                  <div className="appoList">
                    <table>
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Created on</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      {protocolList?.length > 0 ? (
                        <tbody>
                          {protocolList?.map((item, index) => (
                            <tr key={index}>
                              <td className="appoName">{item?.name}</td>
                              <td>{formatDateInDayDateYear(item?.created_at)}</td>
                              <td>
                                <button
                                  className="viewLink"
                                  onClick={() => {
                                    setSelectedProtocol(item?.id);
                                    setProtocolName(item?.name);
                                    setPrescriptionContent(item?.medicine || "");
                                    setProtocolDraftPrescriptionId(item?.source_prescription_id ? String(item.source_prescription_id) : "");
                                    setProtocolEditorSeed(item?.medicine || "");
                                    setProtocolEditorKey(`protocol-edit-${item?.id}-${Date.now()}`);
                                    setShowProtocolModal(true);
                                  }}>
                                  <img src={editIcon} alt="editIcon" />
                                </button>{" "}
                                <button
                                  className="viewLink"
                                  onClick={() => {
                                    setSelectedProtocol(item?.id);
                                    setShowDeleteProtocolPopup(true);
                                  }}>
                                  <img src={deleteIcon} alt="deleteIcon" />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      ) : (
                        <tbody>
                          <tr>
                            <td colSpan="4" className="centeredText">
                              No Protocols found!
                            </td>
                          </tr>
                        </tbody>
                      )}
                    </table>
                  </div>
                </div>
              </div>
            </div>
            <MedicationConfigurationManagement
              configuration={configureMedicationObj}
              isSaving={saveConBtnDis}
              valuesByType={configureMedicationList}
              onAdd={handleAddConfigureMedication}
              onRequestDelete={handleConfigureMedicationDeleteRequest}
              onStartCreate={handleStartConfigureMedicationCreation}
              onToggleValues={handleToggleConfigureMedicationValues}
              onTypeChange={handleConfigureMedicationTypeChange}
              onValueChange={handleConfigureMedicationValueChange}
            />
          </div>
        </div>
      </div>

      {showTemplateModal && (
        <div className="modal TemplateModal" onClick={toggleTemplateModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <button className="CloseButton" onClick={toggleTemplateModal}></button>
              <h3>Download Template</h3>
            </div>
            <div className="modal-body">
              <ul className="template-list downloadSec">
                {adminTemplateList?.length > 0 ? (
                  <>
                    {adminTemplateList?.map((item, index) => (
                      <li key={index}>
                        <input
                          type="checkbox"
                          id={`template-${index}`}
                          className="checkbox"
                          onChange={() => handleCheckboxChange(item?.id)}
                          checked={selectedAdminTemplates.includes(item?.id)}
                        />
                        <div
                          onClick={() => handleCheckboxChange(item?.id)}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="templateImg">
                            <img src={item?.template_image_url ?? ""} alt="Template image" />
                          </div>
                          <h4>{item?.template_name}</h4>
                        </div>
                      </li>
                    ))}
                    <li className="downloadBtnSec">
                      <button className="downloadBtn" onClick={handleDownloadDoctorTemplate} disabled={btnDis}>
                        {btnDis ? "Please wait..." : "Download"}
                      </button>
                    </li>
                  </>
                ) : (
                  <>
                    <div className="notResultText">You've downloaded all available templates. Check back later for more!</div>
                  </>
                )}
              </ul>
            </div>
          </div>
        </div>
      )}
      {showDeleteTempPopup && (
        <div className="popup holidayPopup">
          <div className="popup-content removePop">
            <button className="cancelBtn" onClick={() => setShowDeleteTempPopup(false)}>
              <img src={cancelIcon} alt="cancelIcon" />
            </button>
            <div className="addNursePopup">
              <h2 className="removeHdn">Are you sure you want to remove this template?</h2>

              <div className="removeNurse">
                <div className="nurseBtns">
                  <button className="yesBtn" onClick={() => handleRemoveTemplate()} disabled={deleteBtnDis}>
                    {deleteBtnDis ? "Please wait..." : "Yes"}
                  </button>
                  <button className="noBtn" onClick={() => setShowDeleteTempPopup(false)}>
                    No
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {showInsertTemplateModal && (
        <div className="modal TemplateModal" onClick={() => setShowInsertTemplateModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <button className="CloseButton" onClick={() => setShowInsertTemplateModal(false)}></button>
              <h3>Insert Multiple Templates</h3>
            </div>
            <div className="modal-body">
              <ul className="template-list downloadSec">
                {templateList?.length > 0 ? (
                  <>
                    {templateList?.map((item, index) => (
                      <li key={index}>
                        <input
                          type="checkbox"
                          id={`insert-template-${index}`}
                          className="checkbox"
                          onChange={() => handleSelectTemplateToInsert(item?.id)}
                          checked={selectedTemplatesToInsert.includes(item?.id)}
                        />
                        <div
                          onClick={() => handleSelectTemplateToInsert(item?.id)}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="templateImg">
                            <img src={item?.template_image_url ?? ""} alt="Template image" />
                          </div>
                          <h4>{item?.template_name}</h4>
                        </div>
                      </li>
                    ))}
                    <li className="downloadBtnSec">
                      <button className="downloadBtn" onClick={handleInsertTemplates}>
                        Insert Selected
                      </button>
                    </li>
                  </>
                ) : (
                  <div className="notResultText">No templates available.</div>
                )}
              </ul>
            </div>
          </div>
        </div>
      )}
      {showDeleteProtocolPopup && (
        <div className="popup holidayPopup">
          <div className="popup-content removePop">
            <button className="cancelBtn" onClick={() => setShowDeleteProtocolPopup(false)}>
              <img src={cancelIcon} alt="cancelIcon" />
            </button>
            <div className="addNursePopup">
              <h2 className="removeHdn">Are you sure you want to delete this protocol?</h2>

              <div className="removeNurse">
                <div className="nurseBtns">
                  <button className="yesBtn" onClick={() => handleRemoveProtocol()} disabled={deleteBtnDis}>
                    {deleteBtnDis ? "Please wait..." : "Yes"}
                  </button>
                  <button className="noBtn" onClick={() => setShowDeleteProtocolPopup(false)}>
                    No
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {showDeleteConfigurePopup && (
        <div className="popup holidayPopup">
          <div className="popup-content removePop">
            <button className="cancelBtn" onClick={() => setShowDeleteConfigurePopup(false)}>
              <img src={cancelIcon} alt="cancelIcon" />
            </button>
            <div className="addNursePopup">
              <h2 className="removeHdn">Are you sure you want to delete {configureDeleteMedicationObj?.value}?</h2>

              <div className="removeNurse">
                <div className="nurseBtns">
                  <button className="yesBtn" onClick={() => handleRemoveConfigureMedication()} disabled={deleteBtnDis}>
                    {deleteBtnDis ? "Please wait..." : "Yes"}
                  </button>
                  <button className="noBtn" onClick={() => setShowDeleteConfigurePopup(false)}>
                    No
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {showProtocolModal && (
        <div className="modal TemplateModal TemplateProtocol" onClick={closeProtocolModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <button className="CloseButton" onClick={closeProtocolModal}></button>
              <h3>{selectedProtocol ? "Edit" : "Add"} Protocol</h3>
            </div>
            <div className="modal-body">
              <div className="mainNameSection" style={{ marginBottom: '20px', marginTop: '20px' }}>
                <label style={{ marginRight: '10px' }}>Protocol name: </label>
                <input type="text" placeholder="Enter name..." className="textBox" value={protocolName} onChange={e => setProtocolName(e.target.value)} />
              </div>

              <ProtocolPrescriptorComposer
                key={protocolEditorKey}
                initialTemplate={protocolEditorSeed}
                prescriptionId={protocolDraftPrescriptionId}
                onTemplateChange={setPrescriptionContent}
              />

              <div className="buttonSection">
                {selectedProtocol ? (
                  <button className="saveBtn" disabled={saveProBtnDis} onClick={() => handleEditProtocol()}>
                    {saveProBtnDis ? "Please wait..." : "Save"}
                  </button>
                ) : (
                  <button className="saveBtn" disabled={saveProBtnDis} onClick={() => handleAddProtocol()}>
                    {saveProBtnDis ? "Please wait..." : "Save"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <SpecialTemplatesDownloadModal
        isOpen={showSpecialTemplatesDownloadModal}
        onClose={() => {
          setShowSpecialTemplatesDownloadModal(false);
          setSpecialTemplatesRefreshKey(prev => prev + 1);
        }}
      />
    </div>
  );
};

export default PrescriptionManagement;
